import React, { useState } from 'react'
import { Flex } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'

import {
  ButtonPrimary,
  ButtonClean,
  Modal,
  Checkbox
} from 'front-end/src/components'

import {
  NewMembersSelector,
  IMembersSelectorGroup,
  IMembersSelectorMembers,
  IMembersSelectorUser
} from 'front-end/src/components/common/new-members-selector'
import uniqueId from '../../../../../../components/utils/uniqueId'
import './EventInviteModal.less'
import { SenderSelect } from '../../../../../manage-campaign-page/campaign-new-action/SenderSelect'
import {
  useAddAttendeesTo3dPartyCalendarsLazyQuery,
  useAddAttendeesTo3dPartyCalendarsSeriesLazyQuery,
  useInviteGroupMutation,
  useInviteUserMutation,
  useRecurringInviteGroupMutation,
  useRecurringInviteUserMutation,
  useUsersAndGroupsLazyQuery
} from './EventInviteModal.codegen'
import {
  InvitedGroupFragment,
  useEventInvitedGroupsLazyQuery,
  useRecurringEventInvitedGroupsLazyQuery
} from '../../InvitedGroupsList.codegen'
import { useManageEventContext } from '../../../../ManageEventContext'
import { InviteeItemFragment } from '../AttendanceTab.codegen'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { useAllGroupsLazyQuery } from 'front-end/src/graphql/commonQueries/allGroups.codegen'

type EventInviteModalProps = {
  onHide: () => void
  invitees: InviteeItemFragment[]
  show?: boolean
  onInvite?: () => void
}

type EventInviteModalState = {
  isInviting: boolean
  isSearching: boolean
  membersFound: IMembersSelectorMembers
  sendEmailInvitation: boolean
  emailIdentityId: number | null
}

export const EventInviteModal = ({
  show = false,
  onHide,
  onInvite,
  invitees
}: EventInviteModalProps) => {
  const [selectedMembers, setSelectedMembers] = React.useState({
    users: [] as IMembersSelectorUser[],
    groups: [] as IMembersSelectorGroup[]
  })
  const [addressBook, setAddressBook] = React.useState({
    users: [] as IMembersSelectorUser[],
    groups: [] as IMembersSelectorGroup[]
  })

  const [invitedGroups, setInvitedGroups] = useState<InvitedGroupFragment[]>([])

  const [allGroups] = useAllGroupsLazyQuery({
    onCompleted: (data) => {
      setAddressBook({
        ...addressBook,
        groups: data.groups.map(mapGroupInviteeToMembersSelector) || []
      })
    }
  })

  const [searchUsersAndGroups] = useUsersAndGroupsLazyQuery({
    onCompleted: async (data) => {
      await fetchInvitedGroups({
        variables: { eventId: event.id }
      })

      const membersFound = {
        users: data.users.map(mapUserInviteeToMembersSelector),
        groups: data.groups.map(mapGroupInviteeToMembersSelector)
      }

      changeState({
        membersFound,
        isSearching: false
      })
    }
  })
  const { isRecurringSeries, event } = useManageEventContext()
  const [inviteUser] = useInviteUserMutation()
  const [inviteGroup] = useInviteGroupMutation()
  const [recurringInviteUser] = useRecurringInviteUserMutation()
  const [recurringInviteGroup] = useRecurringInviteGroupMutation()
  const [addAttendeesTo3dPartyCalendars] =
    useAddAttendeesTo3dPartyCalendarsLazyQuery()
  const [addAttendeesTo3dPartyCalendarsSeries] =
    useAddAttendeesTo3dPartyCalendarsSeriesLazyQuery()

  const onFetchInvitedGroups = async (invitedGroupsData) => {
    const eventKey = isRecurringSeries ? 'recurringEvent' : 'event'
    const invitedGroups =
      invitedGroupsData?.admin?.[eventKey]?.invitedGroups || []
    setInvitedGroups(invitedGroups)
    await allGroups()
  }

  const [fetchInvitedGroups] = isRecurringSeries
    ? useRecurringEventInvitedGroupsLazyQuery({
        onCompleted: onFetchInvitedGroups
      })
    : useEventInvitedGroupsLazyQuery({ onCompleted: onFetchInvitedGroups })

  const [state, setState] = useState<EventInviteModalState>({
    isInviting: false,
    isSearching: false,
    membersFound: {
      users: [] as IMembersSelectorUser[],
      groups: [] as IMembersSelectorGroup[]
    },

    sendEmailInvitation: false,
    emailIdentityId: null
  })

  const membersSelectorRef: any = null
  const checkboxId: string = uniqueId()

  const changeState = (changes: Partial<EventInviteModalState>) => {
    setState({
      ...state,
      ...changes
    })
  }

  const mapGroupInviteeToMembersSelector = ({ id, name }) => {
    const isInvited = invitedGroups.find((group) => id === group.id)

    return {
      id,
      name,
      cantBeSelectedReason: (isInvited && t`Already invited`) || undefined
    }
  }

  const mapUserInviteeToMembersSelector = (props) => {
    const isInvited = (invitees as InviteeItemFragment[]).find(
      (invitee) => props.id === invitee.user_id
    )

    return {
      ...props,
      cantBeSelectedReason: (isInvited && t`Already invited`) || undefined
    }
  }

  const reset = () => {
    changeState({
      membersFound: { users: [], groups: [] },
      sendEmailInvitation: false
    })
    setSelectedMembers(() => ({
      users: [],
      groups: []
    }))
    membersSelectorRef?.reset()
  }

  const onInviteFc = () => {
    changeState({
      membersFound: { users: [], groups: [] }
    })
    setSelectedMembers({ users: [], groups: [] })

    if (typeof onInvite === 'function') {
      onInvite()
    }
  }

  const invite = async () => {
    changeState({ isInviting: true })
    const emails: string[] = []
    const groupIds: number[] = []
    try {
      await Promise.all(
        selectedMembers.users.map((user) => {
          emails.push(user.userIdentity)
          const variables = {
            eventId: event.id,
            userId: user.id,
            sendEventInvite: state.sendEmailInvitation,
            emailIdentityId: state.emailIdentityId
          }
          return isRecurringSeries
            ? recurringInviteUser({ variables })
            : inviteUser({ variables })
        })
      )
      await Promise.all(
        selectedMembers.groups.map((group) => {
          groupIds.push(group.id)
          const variables = {
            eventId: event.id,
            groupId: group.id,
            sendEventInvite: state.sendEmailInvitation,
            emailIdentityId: state.emailIdentityId
          }
          return isRecurringSeries
            ? recurringInviteGroup({ variables })
            : inviteGroup({ variables })
        })
      )
      if (isRecurringSeries) {
        await addAttendeesTo3dPartyCalendarsSeries({
          variables: { eventId: event.id, emails, groupIds }
        })
      } else {
        await addAttendeesTo3dPartyCalendars({
          variables: { eventId: event.id, emails, groupIds }
        })
      }
      onInviteFc()
    } catch (err) {
      console.error(err)
      notification.error(t`Something went wrong.`)
    } finally {
      changeState({ isInviting: false })
    }
  }

  const inviteesCount =
    selectedMembers.users.length + selectedMembers.groups.length

  const onQueryChangeCb = async (value) => {
    changeState({
      isSearching: !!value
    })
    await searchUsersAndGroups({
      variables: { search: value }
    })
  }

  return (
    <Modal
      className="EventInviteModal"
      header={t`Invite people to this event`}
      onHide={onHide}
      show={show}
      newDesign
      onShown={async () =>
        fetchInvitedGroups({
          variables: { eventId: event.id }
        })
      }
      onHidden={reset}
      controls={[
        <ButtonClean key={1} onClick={onHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonPrimary
          disabled={inviteesCount === 0 || state.isInviting}
          key={2}
          onClick={invite}
          isLoading={state.isInviting}
        >
          <Trans>Invite</Trans>
        </ButtonPrimary>
      ]}
    >
      {show && (
        <NewMembersSelector
          onQueryChange={onQueryChangeCb}
          changeSelectedMembers={setSelectedMembers}
          selectedMembers={selectedMembers}
          isSearching={state.isSearching}
          membersFound={state.membersFound}
          addressBook={addressBook}
        />
      )}
      <Flex mt={4} direction="column">
        <Flex alignItems="flex-end">
          <Checkbox
            id={checkboxId}
            isChecked={state.sendEmailInvitation}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              changeState({ sendEmailInvitation: e.target.checked })
            }}
          >
            <Trans>Send an invitation via email?</Trans>
          </Checkbox>
        </Flex>
        {state.sendEmailInvitation && (
          <Flex>
            <SenderSelect
              value={state.emailIdentityId}
              onChange={(v) => changeState({ emailIdentityId: v })}
            />
          </Flex>
        )}
      </Flex>
    </Modal>
  )
}
